import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';

import MatterCard from './matterCard';
import { useGetCustomerMattersById } from '../../hooks/useGetCustomerMattersById';
import { MatterStatus } from '../../constants/matter';
import { useInView } from 'react-intersection-observer';
import { Spin } from '../Ant';
import { IMatter } from '../../interface/matter';
import { ICustomerType } from '../../constants/customer';
import NoData from '../dashboard/noData';
import { BriefcaseEmpty } from '../icons';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import SortSelector from '../sortPopover/sortSelector';
import { IActiveSortState, ISortOption } from '../../interface/common';
import SkeletonCardList from '../common/skeleton/skeletonCardList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';
import { MY_OWN_FILTER } from '../../constants/constants';
import { useOwnerFilter } from '../../hooks/useOwnerFilter';
import { useMatterFilters } from '../../hooks/useMatterFilters';

const MatterCardListing = ({ type }: { type?: string }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id: customerId } = useParams();
  const isCompany = type === ICustomerType.COMPANY;
  const [searchParams, setSearchParams] = useSearchParams();
  const ownerFilter = useOwnerFilter({
    isMyOwnDefault: false,
    restoredValue: searchParams.get('matterOwner'),
  });
  const initialSearch = searchParams.get('matterSearch') || '';
  const initialInvoiceableType = searchParams.get('matterInvoiceableType') || '';
  const initialMatterTypeIds = useMemo(
    () => (searchParams.get('matterTypes') || '').split(',').filter(Boolean),
    [],
  );
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const {
    invoiceableTypeFilters,
    matterTypeFilters,
    selectedInvoiceableType,
    setSelectedInvoiceableType,
    selectedMatterTypes,
    setSelectedMatterTypes,
    selectedMatterFilters,
    matterFilterParams,
    removeMatterFilter,
    clearMatterFilters,
    hasMoreMatterTypes,
    isFetchingMoreMatterTypes,
    loadMoreMatterTypes,
  } = useMatterFilters({ initialInvoiceableType, initialMatterTypeIds });
  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'openingDate',
    direction: 'DESC',
  });
  const matterSortOptions: ISortOption[] = useMemo(
    () => [
      {
        field: 'openingDate',
        label: t('matter:openingDate'),
        type: 'date',
        defaultDirection: 'DESC',
      },
      { field: 'subject', label: t('matter:subject'), type: 'text', defaultDirection: 'ASC' },
      {
        field: 'matterNumber',
        label: t('matter:matterNumber'),
        type: 'number',
        defaultDirection: 'ASC',
      },
    ],
    [t],
  );
  const {
    data: matterList,
    isLoading: isMatterLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useGetCustomerMattersById({
    sortBy: [`${activeSort.field}:${activeSort.direction}`],
    customerId,
    type,
    relations: [
      'responsibleUser:fullName',
      'principalCompany:name',
      'principalPrivatePerson:fullName',
      'parties',
      'parties.roles',
      'parties.roles.matterPartyRole',
    ],
    withFavoriteInformation: true,
    withHasSubMatters: true,
    extraFilters: { status: MatterStatus.ACTIVE, ...ownerFilter.apiFilters, ...matterFilterParams },
    search: searchValue,
    fields: [
      'subject',
      'matterNumber',
      'status',
      'invoiceableType',
      'openingDate',
      'hasNotes',
      'hasImportantNotes',
      'hasImportantRelatedNotes',
      'fixedPrice',
      'fixedPriceInUse',
      'parentMatterId',
      'insider',
      'language',
    ],
  });
  const normalizedMatterList = matterList ?? [];
  useEffect(() => {
    setSearchParams({
      matterSearch: searchValue,
      matterOwner: ownerFilter.value,
      matterInvoiceableType: selectedInvoiceableType?.value ?? '',
      matterTypes: selectedMatterTypes.map((item) => item.value).join(','),
    });
  }, [searchValue, ownerFilter.value, selectedInvoiceableType, selectedMatterTypes]);
  const { ref, inView } = useInView({
    threshold: 0.5,
  });
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && normalizedMatterList.length > 0) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, inView, isFetchingNextPage, normalizedMatterList.length]);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" />;
    }
    return null;
  };

  return (
    <CollapsibleListLayout
      testId="customer-matters-listing"
      header={
        <SearchInputWithFilters
          searchTitle={t('matter:search')}
          handleSearch={(val) => {
            setSearchValue(val);
          }}
          searchValue={searchValue}
          filterTitle={t('matter:filter.matterFilters')}
          ownerOptions={ownerFilter.options}
          ownerTitle={ownerFilter.title}
          setSelectedOwner={ownerFilter.select}
          radioTitle={t('matter:filter.invoiceableType')}
          checkboxTitle={t('matter:filter.matterType')}
          radioOptions={invoiceableTypeFilters}
          checkboxOptions={matterTypeFilters}
          selectedFilter={[...ownerFilter.selectedOptions, ...selectedMatterFilters]}
          setSelectedFilter={(val) => setSelectedInvoiceableType(val)}
          setSelectedCheckboxFilter={(val) => setSelectedMatterTypes(val)}
          clearFilter={(val?: string) => {
            if (!val) {
              ownerFilter.reset();
              clearMatterFilters();
              return;
            }
            if (val === MY_OWN_FILTER) {
              ownerFilter.reset();
              return;
            }
            removeMatterFilter(val);
          }}
          hasMoreCheckboxOptions={hasMoreMatterTypes}
          isFetchingMoreCheckboxOptions={isFetchingMoreMatterTypes}
          onLoadMoreCheckboxOptions={loadMoreMatterTypes}
          handleRefresh={() => {
            queryClient.removeQueries({
              queryKey: ['customer-matter-list'],
            });
            return refetch();
          }}
        >
          <SortSelector
            modalLabel={t('common:sorting.title')}
            options={matterSortOptions}
            onApply={(sort) => setActiveSort(sort)}
            activeSort={activeSort}
          />
        </SearchInputWithFilters>
      }
    >
      {normalizedMatterList.length > 0 &&
        normalizedMatterList.map((val: IMatter) => {
          return (
            <MatterCard
              data-testid={`matter-card`}
              key={val?.id}
              matter={val}
              type={isCompany ? ICustomerType.COMPANY : ICustomerType.PRIVATE_PERSON}
            />
          );
        })}
      {hasNextPage && <div ref={ref} className="loading-indicator" />}
      {(!matterList || (normalizedMatterList.length === 0 && !isMatterLoading)) && (
        <div className="flex-grow d-flex justify-center items-center">
          <NoData
            header={t('matter:noMatters')}
            subHeader={t('common:tryChangingSearchCriteria')}
            icon={<BriefcaseEmpty />}
          />
        </div>
      )}
      {isMatterLoading ? (
        <SkeletonCardList rows={[1, 1, 2]} withSubcontentOnFirstRow />
      ) : (
        renderLoader()
      )}
    </CollapsibleListLayout>
  );
};

export default MatterCardListing;
